import { useState } from "react";
import { enAU, enGB, enUS, de, es, fr, pt } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/store/auth.store";
import {
  formatCalendarDate,
  getTodayInUserTimezone,
  getUserLocale,
} from "@/utils/dateFormat";

const parseIso = (value) => {
  if (!value) return undefined;
  // Local midnight — `new Date("YYYY-MM-DD")` parses as UTC and shifts the
  // day for east-of-UTC users (same pitfall as UnbilledUsageSummary).
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? undefined : date;
};

const toIso = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate(),
  ).padStart(2, "0")}`;

const DATE_FNS_LOCALES = {
  "en-AU": enAU,
  "en-GB": enGB,
  "en-US": enUS,
  de,
  es,
  fr,
  pt,
};

const resolveDayPickerLocale = (localeTag) =>
  DATE_FNS_LOCALES[localeTag] ||
  DATE_FNS_LOCALES[localeTag?.split("-")[0]] ||
  enAU;

/**
 * Drop-in replacement for `<Input type="date">` backed by the theme-styled
 * Calendar. Speaks the native input's contract: `value` is a local
 * "YYYY-MM-DD" string (or ""), and `onChange` receives `{ target: { value } }`
 * so existing `e.target.value` handlers keep working. `min`/`max` take
 * "YYYY-MM-DD" strings like the native attribute. Unless `required`, a Clear
 * action is offered when a date is set.
 */
const DatePicker = ({
  value,
  onChange,
  min,
  max,
  disabled = false,
  required = false,
  placeholder,
  className,
  id,
  "aria-label": ariaLabel,
}) => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const [open, setOpen] = useState(false);
  const selected = parseIso(value);
  const minDate = parseIso(min);
  const maxDate = parseIso(max);

  const emit = (nextValue) => onChange?.({ target: { value: nextValue } });

  const disabledMatchers = [
    ...(minDate ? [{ before: minDate }] : []),
    ...(maxDate ? [{ after: maxDate }] : []),
  ];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          disabled={disabled}
          aria-label={ariaLabel}
          className={cn(
            "flex h-9 w-full items-center gap-2 rounded-md border border-input bg-background px-3 py-1 text-left text-base shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
            !selected && "text-muted-foreground",
            className,
          )}
        >
          <CalendarIcon className="h-4 w-4 shrink-0 opacity-60" />
          <span className="truncate">
            {selected
              ? formatCalendarDate(selected, user)
              : placeholder || t("datePicker.placeholder", "Pick a date")}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selected}
          defaultMonth={selected || maxDate || minDate}
          locale={resolveDayPickerLocale(getUserLocale(user))}
          today={getTodayInUserTimezone(user)}
          captionLayout="dropdown"
          startMonth={minDate || new Date(1900, 0)}
          endMonth={maxDate || new Date(new Date().getFullYear() + 10, 11)}
          disabled={disabledMatchers.length ? disabledMatchers : undefined}
          autoFocus
          onSelect={(date) => {
            if (date) {
              emit(toIso(date));
              setOpen(false);
            }
          }}
        />
        {selected && !required && (
          <div className="border-t border-border p-2 text-right">
            <button
              type="button"
              className="text-xs text-muted-foreground hover:text-foreground"
              onClick={() => {
                emit("");
                setOpen(false);
              }}
            >
              {t("datePicker.clear", "Clear")}
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
DatePicker.displayName = "DatePicker";

export { DatePicker };
